import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import {
  Search,
  Calendar,
  MapPin,
  Users,
  Building2,
  Monitor,
  Laptop,
  FileText,
  X,
  ArrowRight,
  ArrowLeft,
  Check,
  Loader2,
  Shield,
  ChevronDown,
  ChevronUp,
  CalendarIcon
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { AppShell3 } from '@/components/app-shell-3';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ProgressSteps } from '@/components/ProgressSteps';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AsyncCombobox } from '@/components/ui/async-combobox';
import { CreateCustomCourse } from '@/components/modals/qualifications/CreateCustomCourse';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';

const progressSteps = [
  { title: 'Course & Type', description: 'Select training' },
  { title: 'Details', description: 'Dates & delegates' },
  { title: 'Review', description: 'Confirm & submit' }
];

export function QuoteBuilderPage() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const { user, userProfile, company } = useAuth();

  // Set page title
  usePageTitle('Build Quote');

  const [currentStep, setCurrentStep] = useState(1);
  const [animationDirection, setAnimationDirection] = useState(0);
  const [loading, setLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [courseSearch, setCourseSearch] = useState('');
  const [courseDisplayLabel, setCourseDisplayLabel] = useState('');
  const [delegateSearch, setDelegateSearch] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Create custom course modal state
  const [isCreateCourseOpen, setIsCreateCourseOpen] = useState(false);
  const [newCourseTitle, setNewCourseTitle] = useState('');
  const [previousCourseName, setPreviousCourseName] = useState('');

  // Selected course state
  const [selectedCourseData, setSelectedCourseData] = useState(null);

  const [formData, setFormData] = useState({
    courseId: '',
    courseName: '',
    trainingType: ['onsite'],
    trainingLocation: '',
    dateType: 'flexible', // 'flexible', 'specific', 'asap'
    flexibleMonths: '',
    specificDates: [],
    delegateType: 'count', // 'count' or 'members'
    delegateCount: '',
    selectedDelegates: [],
    notes: ''
  });

  const [space, setSpace] = useState(null);
  const [globalTrainers, setGlobalTrainers] = useState([]);

  useEffect(() => {
    if (projectId) {
      fetchTeamMembers(projectId);
      fetchSpaceData(projectId);
    }
  }, [projectId]);

  // Fetch global trainers (BOHSA trainers with company_id = null)
  useEffect(() => {
    const fetchGlobalTrainers = async () => {
      try {
        const { data, error } = await supabase
          .from('trainers')
          .select('*')
          .is('company_id', null)
          .order('display_order', { ascending: true });

        if (!error && data) {
          // Transform trainers into the required JSON structure
          const formattedTrainers = data.map(trainer => ({
            id: trainer.id,
            name: trainer.trainer_name,
            type: 'global', // Mark as global trainer
            email: trainer.booking_email,
            wasNotified: false // Default to false, will be set to true when quote is sent
          }));
          setGlobalTrainers(formattedTrainers);
        }
      } catch (error) {
        console.error('Error fetching global trainers:', error);
      }
    };

    fetchGlobalTrainers();
  }, []);

  // Pre-fill form from URL search params
  useEffect(() => {
    const courseName = searchParams.get('course');
    const courseId = searchParams.get('courseId');
    const courseType = searchParams.get('courseType');
    const delegateId = searchParams.get('delegate');

    if (courseName) {
      setCourseSearch(courseName);
      setCourseDisplayLabel(courseName);

      // If we have a courseId from course_links, use it directly
      if (courseId && courseType === 'verified') {
        const fetchCourseData = async () => {
          try {
            // Set course data with the ID we already have
            setSelectedCourseData({
              id: courseId,
              title: courseName,
              _isVerifiedCourse: true
            });

            // Directly fetch course versions using the ID
            // Course versions removed
          } catch (error) {
            console.error('Error fetching course versions:', error);
          }
        };

        fetchCourseData();
      } else {
        // Fallback: Search for parent course by title (for backward compatibility)
        const fetchParentCourse = async () => {
          try {
            const response = await fetch(
              `https://leqtvmuqmyxkdkaxxfqu.supabase.co/functions/v1/api/courses?search=${encodeURIComponent(courseName)}`,
              {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/json',
                  apikey: 'bohsa-api-xDZ8kuCTQz3axI8VhhjJOQoW3YMB0jcX'
                }
              }
            );

            const result = await response.json();

            if (result?.data && result.data.id) {
              const parentCourse = result.data;

              // Mark as verified course and set selected course data
              setSelectedCourseData({ ...parentCourse, _isVerifiedCourse: true });

              // Fetch course versions
              // Course versions removed
            } else {
              // If no parent course found, it might be a custom course
              console.log('No parent course found for:', courseName);
            }
          } catch (error) {
            console.error('Error fetching parent course:', error);
          }
        };

        fetchParentCourse();
      }
    }

    // Pre-select delegate if provided and team members are loaded
    if (delegateId && teamMembers.length > 0) {
      const delegateExists = teamMembers.find(m => m.id === delegateId);
      if (delegateExists) {
        setFormData(prev => ({
          ...prev,
          delegateType: 'members',
          selectedDelegates: [delegateId]
        }));
      }
    }
  }, [searchParams, teamMembers]);

  const fetchSpaceData = async (projectId) => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', projectId)
        .single();

      if (!error && data) {
        setSpace(data);
      }
    } catch (error) {
      console.error('Error fetching project:', error);
    }
  };

  const fetchTeamMembers = async projectId => {
    try {
      const { data: members, error } = await supabase
        .from('space_members')
        .select(`
          *,
          user_profiles!user_id (
            id,
            first_name,
            last_name,
            email,
            avatar_url,
            job_title
          )
        `)
        .eq('project_id', projectId);

      if (!error && members) {
        // Transform to match expected format
        const formattedMembers = members.map(sm => ({
          id: sm.user_id,
          name: `${sm.user_profiles.first_name || ''} ${sm.user_profiles.last_name || ''}`.trim() || sm.user_profiles.email,
          email: sm.user_profiles.email,
          role: sm.user_profiles.job_title,
          department: sm.department,
          status: sm.status,
          avatar_url: sm.user_profiles.avatar_url,
          user_id: sm.user_id
        })).sort((a, b) => a.name.localeCompare(b.name));

        setTeamMembers(formattedMembers);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  // Handle create new custom course
  const handleCreateNewCourse = searchTerm => {
    setPreviousCourseName(courseSearch);
    setNewCourseTitle(searchTerm);
    setIsCreateCourseOpen(true);
  };

  // Handle Create Custom Course modal cancellation (close without creating)
  const handleCreateCourseCancel = () => {
    setIsCreateCourseOpen(false);
    setCourseSearch(previousCourseName);
    setCourseDisplayLabel(previousCourseName);
  };

  // Handle Create Custom Course modal close (generic)
  const handleCreateCourseClose = () => {
    setIsCreateCourseOpen(false);
  };

  // Handle successful custom course creation
  const handleCustomCourseCreated = createdCourse => {
    setCourseSearch(createdCourse.title);
    setCourseDisplayLabel(createdCourse.title);
    setFormData(prev => ({
      ...prev,
      courseName: createdCourse.title
    }));
    setSelectedCourseData(null); // Custom courses don't have versions
    toast.success(`"${createdCourse.title}" has been selected`);
  };


  // Search handler for qualifications/courses
  const handleCourseSearch = async searchTerm => {
    if (!searchTerm || searchTerm.trim() === '') {
      return [];
    }

    try {
      const requestBody = {
        query: searchTerm
      };

      // Parallel API calls
      const [apiResults, dbResults] = await Promise.all([
        // Call 1: Custom API endpoint for parent courses
        fetch(
          `https://leqtvmuqmyxkdkaxxfqu.supabase.co/functions/v1/api/courses?search=${searchTerm}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              apikey: 'bohsa-api-xDZ8kuCTQz3axI8VhhjJOQoW3YMB0jcX'
            }
          }
        )
          .then(res => res.json())
          .then(response => {
            // API returns { data: [...], message: "...", type: "..." }
            // Extract the courses array from response.data
            const courses = response?.data || [];

            // Transform API results to { value, label, group, data } format
            if (Array.isArray(courses)) {
              return courses.map(course => {
                // Add a marker to identify this as a verified course
                return {
                  value: course.name,
                  label: course.name,
                  group: 'Verified Courses',
                  data: { ...course, _isVerifiedCourse: true }
                };
              });
            }
            return [];
          })
          .catch(err => {
            console.error('API search error:', err);
            return [];
          }),

        // Call 2: Supabase custom_courses table
        supabase
          .from('custom_courses')
          .select('*')
          .ilike('title', `%${searchTerm}%`)
          .or(`company_id.eq.${company?.id},company_id.is.null`)
          .limit(10)
          .then(({ data, error }) => {
            if (error) {
              console.error('Database search error:', error);
              return [];
            }

            // Transform DB results to { value, label, group, data } format
            return (data || []).map(course => ({
              value: course.title || course.name,
              label: course.title || course.name,
              group: 'Custom Courses',
              data: course
            }));
          })
      ]);

      // Combine and deduplicate results
      const combinedResults = [...apiResults, ...dbResults];

      // Remove duplicates based on value (title)
      const uniqueResults = combinedResults.reduce((acc, current) => {
        const exists = acc.find(item => item.value === current.value);
        if (!exists) {
          acc.push(current);
        }
        return acc;
      }, []);

      return uniqueResults;
    } catch (error) {
      console.error('Search error:', error);
      return [];
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setAnimationDirection(1);
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setAnimationDirection(-1);
      setCurrentStep(prev => Math.max(prev - 1, 1));
    }
  };

  const validateStep = step => {
    switch (step) {
      case 1:
        if (!courseSearch.trim()) {
          toast.error('Please enter a course name');
          return false;
        }
        return true;
      case 2:
        if (formData.dateType === 'flexible' && !formData.flexibleMonths.trim()) {
          toast.error('Please enter flexible date preferences');
          return false;
        }
        if (formData.dateType === 'specific' && formData.specificDates.length === 0) {
          toast.error('Please select at least one date');
          return false;
        }
        // 'asap' doesn't need validation
        if (
          (formData.trainingType.includes('onsite') ||
            formData.trainingType.includes('classroom')) &&
          !formData.trainingLocation
        ) {
          toast.error('Please enter a training location');
          return false;
        }
        if (formData.delegateType === 'count' && !formData.delegateCount) {
          toast.error('Please enter number of delegates');
          return false;
        }
        if (formData.delegateType === 'members' && formData.selectedDelegates.length === 0) {
          toast.error('Please select at least one team member');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const selectedDelegateNames = formData.selectedDelegates
        .map(id => teamMembers.find(m => m.id === id)?.name)
        .filter(Boolean);

      // Format date preferences based on date type
      let datePrefs = null;
      if (formData.dateType === 'flexible') {
        datePrefs = formData.flexibleMonths;
      } else if (formData.dateType === 'specific' && formData.specificDates.length > 0) {
        // Format multiple dates as comma-separated list
        datePrefs = formData.specificDates
          .sort((a, b) => a - b)
          .map(date => format(date, 'yyyy-MM-dd'))
          .join(', ');
      } else if (formData.dateType === 'asap') {
        datePrefs = 'ASAP';
      }


      // Get delegate count
      const delegateCount = formData.selectedDelegates.length || parseInt(formData.delegateCount) || 0;

      // Create delegate list for additional details
      const delegatesList = selectedDelegateNames.length > 0
        ? selectedDelegateNames.join(', ')
        : `${delegateCount} delegate${delegateCount !== 1 ? 's' : ''}`;

      // Create webhook payload (for when quote is sent to CRM)
      const locationsValue = formData.trainingLocation || formData.trainingType.join(', ');

      const webhookPayload = {
        requested_date: new Date().toISOString(),
        course_name: courseSearch,
        delegates: delegateCount,
        date_prefs: datePrefs,
        locations: locationsValue,
        additional_details: `<p><strong>Requested by:</strong> ${user?.user_metadata?.first_name || ''} ${user?.user_metadata?.last_name || ''} (${user?.email || 'Unknown'})</p>
                      <p><strong>Delegates:</strong> ${delegatesList} (${delegateCount} total)</p>
                      <p><strong>Project:</strong> ${space?.name || 'Unknown Project'}</p>
                      ${selectedCourseData?.id ? `<p><strong>Parent Course ID:</strong> ${selectedCourseData.id}</p>` : ''}
                      ${formData.notes ? `<p><strong>Additional notes:</strong></p><p>${formData.notes}</p>` : ''}`
      };

      const quoteData = {
        project_id: projectId,
        course_name: courseSearch,
        course_id: selectedCourseData?.id || null,
        course_versions: null,
        date_prefs: datePrefs,
        delegates: delegateCount,
        delegate_names: selectedDelegateNames,
        locations: locationsValue,
        delivery_methods: formData.trainingType,
        further_info: formData.notes || null,
        requested_by: userProfile?.id || null,
        requested_date: new Date().toISOString(),
        status: 'draft', // Mark as draft - not sent to CRM yet
        created_date: new Date().toISOString(),
        company_id: company?.id,
        webhook_payload: webhookPayload, // Store for later sending to CRM
        selected_trainers: globalTrainers // Pre-fill with global BOHSA trainers
      };

      const { data, error } = await supabase
        .from('training_quotes')
        .insert(quoteData)
        .select()
        .single();

      if (error) throw error;

      // Create compliance_assignments and update user_qualifications for selected delegates
      if (data && formData.selectedDelegates.length > 0) {
        // Try to find a matching requirement in compliance_requirements
        // First try by course_link_id if we have a courseId from URL params
        const courseId = searchParams.get('courseId');
        const courseType = searchParams.get('courseType');

        let matchingRequirement = null;

        if (courseId && courseType === 'verified') {
          // Try to match by course_link_id first (more accurate)
          // IMPORTANT: Select course_link fields for cross-space qualification matching
          console.log('Attempting to match requirement by course_link_id:', courseId);
          const { data: linkMatch } = await supabase
            .from('compliance_requirements')
            .select('id, course_link_id, course_link_type')
            .eq('project_id', projectId)
            .eq('course_link_id', courseId)
            .eq('course_link_type', courseType)
            .maybeSingle();

          matchingRequirement = linkMatch;

          if (linkMatch) {
            console.log('✅ Found requirement by course_link_id:', linkMatch.id);
          } else {
            console.log('⚠️ No requirement found by course_link_id, falling back to name matching');
          }
        }

        // If no courseId match, fall back to name matching
        if (!matchingRequirement) {
          console.log('Attempting to match requirement by name:', courseSearch);
          const { data: nameMatch } = await supabase
            .from('compliance_requirements')
            .select('id, course_link_id, course_link_type')
            .eq('project_id', projectId)
            .ilike('name', `%${courseSearch}%`)
            .maybeSingle();

          matchingRequirement = nameMatch;

          if (nameMatch) {
            console.log('✅ Found requirement by name:', nameMatch.id);
          } else {
            console.log('⚠️ No requirement found by name');
          }
        }

        if (matchingRequirement) {
          // Create/update assignments for each selected delegate (tracking flag only)
          const assignmentsToUpsert = formData.selectedDelegates.map(delegateId => ({
            project_id: projectId,
            requirement_id: matchingRequirement.id,
            team_member_id: delegateId,
            assigned: true,
            quote_id: data.id // Link to the created quote
          }));

          const { error: assignmentError } = await supabase
            .from('compliance_assignments')
            .upsert(assignmentsToUpsert, {
              onConflict: 'requirement_id,team_member_id,project_id'
            });

          if (assignmentError) {
            console.error('Error creating compliance assignments:', assignmentError);
            // Don't throw - quote was created successfully
          }

          // Update user_qualifications renewal_status for each delegate
          for (const delegateId of formData.selectedDelegates) {
            // delegateId is the user_id (compliance_assignments stores user_ids)
            const userId = delegateId;

            if (userId) {
              // Check if user_qualification exists
              const { data: existingQual } = await supabase
                .from('user_qualifications')
                .select('id')
                .eq('user_id', userId)
                .eq('requirement_id', matchingRequirement.id)
                .maybeSingle();

              if (existingQual) {
                // Update existing qualification renewal_status
                await supabase
                  .from('user_qualifications')
                  .update({
                    renewal_status: 'quoting',
                    updated_date: new Date().toISOString()
                  })
                  .eq('id', existingQual.id);
              } else {
                // Create new qualification with renewal_status (status NULL = missing)
                // IMPORTANT: Include course_link fields for cross-space qualification matching
                await supabase
                  .from('user_qualifications')
                  .insert({
                    user_id: userId,
                    requirement_id: matchingRequirement.id,
                    course_link_id: matchingRequirement.course_link_id,
                    course_link_type: matchingRequirement.course_link_type,
                    status: null, // NULL = missing (no evidence yet)
                    renewal_status: 'quoting',
                    created_date: new Date().toISOString(),
                    updated_date: new Date().toISOString()
                  });
              }
            }
          }
        }
      }

      toast.success('Quote created successfully!');

      // Navigate back to where the user came from, or default to bookings
      const fromPath = searchParams.get('from');
      if (fromPath) {
        navigate(fromPath);
      } else {
        navigate(`/space/${projectId}/bookings`);
      }
    } catch (error) {
      console.error('Error creating quote:', error);
      toast.error('Failed to create quote');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    if (currentStep === 1) {
      return courseSearch.trim() !== '';
    }
    return true;
  };

  const filteredDelegates = delegateSearch.trim()
    ? teamMembers.filter(member =>
        member.name.toLowerCase().includes(delegateSearch.toLowerCase())
      )
    : teamMembers;

  const handleReturnToSpace = () => {
    const fromPath = searchParams.get('from');
    if (fromPath) {
      navigate(fromPath);
    } else {
      navigate(`/space/${projectId}`);
    }
  };

  return (
    <AppShell3 breadcrumbs={[]} pageTitle='' pageDescription=''>
      <div className='max-w-7xl w-full mx-auto px-4 py-8'>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className='bg-white rounded-xl shadow-xl border overflow-hidden'
        >
          {/* Header Section */}
          <div className='bg-gradient-to-br from-gray-50 to-white border-b px-8 pt-4 pb-6'>
            {/* Header Grid: Exit button left, Title center, empty right for balance */}
            <div className='grid grid-cols-[auto_1fr_auto] items-start gap-4 mb-4'>
              <Button
                variant='ghost'
                size='sm'
                onClick={handleReturnToSpace}
                className='gap-2'
              >
                <ArrowLeft className='h-4 w-4' />
                Exit
              </Button>

              {/* Title and Subtitle - centered */}
              <div className='text-center'>
                <motion.h1
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.3 }}
                  className='text-3xl font-bold text-gray-900 mb-2'
                >
                  Create Training Quote
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.3 }}
                  className='text-gray-600'
                >
                  Build your custom training quote in just a few steps
                </motion.p>
              </div>

              {/* Empty div for grid balance */}
              <div className='w-[52px]' />
            </div>

            {/* Progress Steps */}
            <div className='max-w-4xl mx-auto'>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.3 }}
              >
                <ProgressSteps
                  steps={progressSteps}
                  currentStep={currentStep}
                />
              </motion.div>
            </div>
          </div>

          {/* Content Area */}
          <div className='px-8 py-8'>
            <AnimatePresence mode='wait'>
              {/* Step 1: Course & Type */}
              {currentStep === 1 && (
                <motion.div
                  key='step1'
                  initial={{
                    opacity: 0,
                    x:
                      animationDirection === 0
                        ? 0
                        : animationDirection === -1
                        ? -100
                        : 100
                  }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className='max-w-4xl mx-auto'
                >
                  <div className='space-y-6'>
                    {/* Course Search */}
                    <div className='space-y-2'>
                      <Label className='text-base font-medium'>
                        Training Course *
                      </Label>
                      <AsyncCombobox
                        value={courseSearch}
                        displayLabel={courseDisplayLabel}
                        onValueChange={value => {
                          setCourseSearch(value);
                          // Clear display label when user types
                          if (!value) {
                            setCourseDisplayLabel('');
                          }
                        }}
                        onSelectionChange={courseData => {
                          // Update courseSearch to display the selected course name
                          const courseName = courseData?.title || courseData?.name;
                          if (courseName) {
                            setCourseSearch(courseName);
                            setCourseDisplayLabel(courseName);
                          }

                          setSelectedCourseData(courseData);

                          // If it's a verified course (marked with _isVerifiedCourse), fetch versions
                          // Versions logic removed
                        }}
                        onSearch={handleCourseSearch}
                        onCreateNew={handleCreateNewCourse}
                        createNewLabel='Create custom course'
                        createNewGroup='Custom Courses'
                        groupConfig={{
                          'Verified Courses': {
                            icon: Shield,
                            color: '#3b82f6' // blue-500
                          }
                        }}
                        placeholder='Search for a course...'
                        emptyText='No courses found. Try a different search term.'
                      />
                    </div>


                    {/* Training Type */}
                    <div className='space-y-3'>
                      <Label className='text-base font-medium'>
                        Delivery Type *
                      </Label>
                      <div className='flex gap-2'>
                                                {[{
                                                  value: 'onsite',
                                                  label: 'Onsite',
                                                  icon: MapPin
                                                },
                                                {
                                                  value: 'classroom',
                                                  label: 'Classroom',
                                                  icon: Building2
                                                },
                                                {
                                                  value: 'remote',
                                                  label: 'Remote',
                                                  icon: Monitor
                                                },
                                                {
                                                  value: 'elearning',
                                                  label: 'E-Learning',
                                                  icon: Laptop
                                                }].map(type => {
                          const Icon = type.icon;
                          return (
                            <button
                              key={type.value}
                              type='button'
                              onClick={() =>
                                setFormData(prev => {
                                  const currentTypes = prev.trainingType;
                                  const isSelected = currentTypes.includes(type.value);
                                  let newTrainingTypes;
                                  let newTrainingLocation = prev.trainingLocation;

                                  if (isSelected) {
                                    // Remove from selection
                                    newTrainingTypes = currentTypes.filter(t => t !== type.value);
                                  } else {
                                    // Add to selection
                                    newTrainingTypes = [...currentTypes, type.value];
                                  }

                                  const needsLocation = newTrainingTypes.includes('onsite') || newTrainingTypes.includes('classroom');
                                  const hadLocation = currentTypes.includes('onsite') || currentTypes.includes('classroom');

                                  if (!needsLocation) {
                                    // No location-based delivery selected, clear it
                                    newTrainingLocation = '';
                                  } else if (needsLocation && !hadLocation && !newTrainingLocation && space?.location) {
                                    // Just added onsite/classroom and location is empty — auto-fill from space
                                    newTrainingLocation = space.location;
                                  }

                                  return {
                                    ...prev,
                                    trainingType: newTrainingTypes,
                                    trainingLocation: newTrainingLocation
                                  };
                                })
                              }
                              className={cn(
                                'flex-1 p-3 rounded-lg border-2 transition-all text-center',
                                formData.trainingType.includes(type.value)
                                  ? 'border-blue-400 bg-blue-50/30 shadow-lg shadow-blue-100/50'
                                  : 'border-gray-200/50 bg-white/50 hover:border-gray-300/50 hover:shadow-md'
                              )}
                            >
                              <div className='flex flex-col items-center gap-2'>
                                <div
                                  className={cn(
                                    'p-2 rounded-lg transition-colors',
                                    formData.trainingType.includes(type.value)
                                      ? 'bg-blue-500 text-white'
                                      : 'bg-gray-100 text-gray-600'
                                  )}
                                >
                                  <Icon className='h-4 w-4' />
                                </div>
                                <div
                                  className={cn(
                                    'text-sm font-semibold transition-colors',
                                    formData.trainingType.includes(type.value)
                                      ? 'text-blue-900'
                                      : 'text-gray-900'
                                  )}
                                >
                                  {type.label}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Details */}
              {currentStep === 2 && (
                <motion.div
                  key='step2'
                  initial={{
                    opacity: 0,
                    x: animationDirection === 1 ? 100 : -100
                  }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{
                    opacity: 0,
                    x: animationDirection === 1 ? -100 : 100
                  }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className='max-w-4xl mx-auto space-y-6'
                >
                  {/* Training Location (conditional) */}
                  {(formData.trainingType.includes('onsite') ||
                    formData.trainingType.includes('classroom')) && (
                    <div className='space-y-2'>
                      <Label className='text-base font-medium flex items-center gap-2'>
                        <MapPin className='h-4 w-4' />
                        Training Location *
                      </Label>
                      <Input
                        type='text'
                        value={formData.trainingLocation}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            trainingLocation: e.target.value
                          }))
                        }
                        placeholder='Enter full address or location details...'
                        className='bg-white border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all'
                      />
                      {space?.location && (
                        <div className="mt-2">
                          <Badge
                            variant="outline"
                            onClick={() => setFormData(prev => ({ ...prev, trainingLocation: space.location }))}
                            className="cursor-pointer hover:bg-gray-100"
                          >
                            Use Space Location: {space.location.split(',').slice(0, 2).join(', ').trim()}
                          </Badge>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Preferred Date */}
                  <div className='space-y-3'>
                    <Label className='text-base font-medium'>
                      Preferred Date *
                    </Label>

                    {/* Date Type Buttons */}
                    <div className='flex gap-2'>
                      <Button
                        type='button'
                        variant={formData.dateType === 'flexible' ? 'default' : 'outline'}
                        size='sm'
                        onClick={() => setFormData(prev => ({ ...prev, dateType: 'flexible' }))}
                        className='flex-1'
                      >
                        Flexible dates
                      </Button>
                      <Button
                        type='button'
                        variant={formData.dateType === 'specific' ? 'default' : 'outline'}
                        size='sm'
                        onClick={() => setFormData(prev => ({ ...prev, dateType: 'specific' }))}
                        className='flex-1'
                      >
                        Selected dates
                      </Button>
                      <Button
                        type='button'
                        variant={formData.dateType === 'asap' ? 'default' : 'outline'}
                        size='sm'
                        onClick={() => setFormData(prev => ({ ...prev, dateType: 'asap' }))}
                        className='flex-1'
                      >
                        ASAP
                      </Button>
                    </div>

                    {/* Date Inputs Based on Selection */}
                    {formData.dateType === 'flexible' && (
                      <Input
                        type='text'
                        value={formData.flexibleMonths}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            flexibleMonths: e.target.value
                          }))
                        }
                        placeholder='e.g., January-March 2025'
                        className='bg-white border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all'
                      />
                    )}

                    {formData.dateType === 'specific' && (
                      <div className='space-y-3'>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant='outline'
                              className={cn(
                                'w-full justify-start text-left font-normal bg-white',
                                !formData.specificDates?.length && 'text-muted-foreground'
                              )}
                            >
                              <CalendarIcon className='mr-2 h-4 w-4' />
                              {formData.specificDates?.length > 0
                                ? `${formData.specificDates.length} date${formData.specificDates.length > 1 ? 's' : ''} selected`
                                : 'Select dates'
                              }
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className='w-auto p-0' align='start'>
                            <CalendarComponent
                              mode='multiple'
                              selected={formData.specificDates}
                              onSelect={dates =>
                                setFormData(prev => ({
                                  ...prev,
                                  specificDates: dates || []
                                }))
                              }
                              disabled={date =>
                                date < new Date(new Date().setHours(0, 0, 0, 0))
                              }
                              initialFocus
                              className='rounded-md border'
                            />
                          </PopoverContent>
                        </Popover>

                        {formData.specificDates?.length > 0 && (
                          <div className='space-y-1.5'>
                            <Label className='text-xs text-muted-foreground'>
                              Selected dates:
                            </Label>
                            <div className='flex flex-wrap gap-2'>
                              {formData.specificDates
                                .sort((a, b) => a - b)
                                .map((date, index) => (
                                  <div
                                    key={index}
                                    className='inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-200 rounded-md text-sm'
                                  >
                                    <Calendar className='h-3 w-3 text-blue-600' />
                                    <span className='text-blue-900'>
                                      {format(date, 'MMM dd, yyyy')}
                                    </span>
                                    <button
                                      onClick={() =>
                                        setFormData(prev => ({
                                          ...prev,
                                          specificDates: prev.specificDates.filter(
                                            (_, i) => i !== index
                                          )
                                        }))
                                      }
                                      className='ml-1 rounded-full hover:bg-blue-100 p-0.5 transition-colors'
                                    >
                                      <X className='h-3 w-3 text-blue-600' />
                                    </button>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {formData.dateType === 'asap' && (
                      <div className='p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-900'>
                        Training needed as soon as possible
                      </div>
                    )}
                  </div>

                  {/* Delegates Selection */}
                  <div className='space-y-2'>
                    <Label className='text-base font-medium mb-3'>Delegates *</Label>
                    <div className='grid grid-cols-[auto_1fr] gap-4 items-start'>
                      {/* Column 1: Delegate Type Select */}
                      <Select
                        value={formData.delegateType}
                        onValueChange={value => {
                          setFormData(prev => ({
                            ...prev,
                            delegateType: value,
                            // Clear the other field when switching
                            delegateCount: value === 'count' ? prev.delegateCount : '',
                            selectedDelegates: value === 'members' ? prev.selectedDelegates : []
                          }));
                        }}
                      >
                        <SelectTrigger className='bg-white w-48'>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='count'>Number of Delegates</SelectItem>
                          <SelectItem value='members'>Select Team Members</SelectItem>
                        </SelectContent>
                      </Select>

                      {/* Column 2: Input based on type */}
                      <div>
                        {formData.delegateType === 'count' ? (
                          <Input
                            type='number'
                            min='1'
                            value={formData.delegateCount}
                            onChange={e =>
                              setFormData(prev => ({
                                ...prev,
                                delegateCount: e.target.value
                              }))
                            }
                            placeholder='e.g. 10'
                            className='bg-white border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all'
                          />
                        ) : (
                          <div className='space-y-2'>
                            <div className='relative'>
                              <Search className='absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground' />
                              <Input
                                type='text'
                                value={delegateSearch}
                                onChange={e => setDelegateSearch(e.target.value)}
                                onFocus={() => setIsDropdownOpen(true)}
                                placeholder='Search team members...'
                                className='pl-8 bg-white text-sm h-9'
                              />
                            </div>

                            {isDropdownOpen && (
                              <div className='border rounded-lg shadow-lg bg-white'>
                                <ScrollArea className='h-[200px]'>
                                  <div className='p-1'>
                                    {filteredDelegates
                                      .filter(member => {
                                        const query = delegateSearch.toLowerCase();
                                        return member.name
                                          .toLowerCase()
                                          .includes(query);
                                      })
                                      .map(member => {
                                        const isSelected =
                                          formData.selectedDelegates.includes(
                                            member.id
                                          );

                                        return (
                                          <div
                                            key={member.id}
                                            onClick={() => {
                                              setFormData(prev => ({
                                                ...prev,
                                                selectedDelegates: isSelected
                                                  ? prev.selectedDelegates.filter(
                                                      id => id !== member.id
                                                    )
                                                  : [
                                                      ...prev.selectedDelegates,
                                                      member.id
                                                    ]
                                              }));
                                            }}
                                            className='flex items-center space-x-2 px-2 py-1.5 rounded cursor-pointer hover:bg-gray-50'
                                          >
                                            <div className='flex items-center justify-center w-4 h-4'>
                                              {isSelected && (
                                                <Check className='h-3.5 w-3.5 text-green-600' />
                                              )}
                                            </div>
                                            <div className='flex-1 min-w-0'>
                                              <p className='text-sm font-medium truncate'>
                                                {member.name}
                                              </p>
                                              {member.role && (
                                                <p className='text-xs text-muted-foreground truncate'>
                                                  {member.role}
                                                </p>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    {filteredDelegates.filter(member => {
                                      const query = delegateSearch.toLowerCase();
                                      return member.name
                                        .toLowerCase()
                                        .includes(query);
                                    }).length === 0 && (
                                      <div className='text-center py-3'>
                                        <p className='text-xs text-muted-foreground'>
                                          No members found matching "
                                          {delegateSearch}"
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                </ScrollArea>
                                <div className='border-t p-1.5'>
                                  <Button
                                    size='sm'
                                    variant='ghost'
                                    className='w-full h-8 text-xs'
                                    onClick={() => setIsDropdownOpen(false)}
                                  >
                                    Done
                                  </Button>
                                </div>
                              </div>
                            )}

                            {/* Selected Delegates */}
                            {formData.selectedDelegates.length > 0 && (
                              <div className='space-y-1.5'>
                                <Label className='text-xs text-muted-foreground'>
                                  {formData.selectedDelegates.length} selected
                                </Label>
                                <div className='flex flex-wrap gap-1.5'>
                                  {formData.selectedDelegates.map(delegateId => {
                                    const member = teamMembers.find(
                                      m => m.id === delegateId
                                    );
                                    return member ? (
                                      <Badge
                                        key={delegateId}
                                        variant='secondary'
                                        className='pl-1.5 pr-1 py-0.5 text-xs'
                                      >
                                        <span className='mr-1'>{member.name}</span>
                                        <button
                                          onClick={() =>
                                            setFormData(prev => ({
                                              ...prev,
                                              selectedDelegates:
                                                prev.selectedDelegates.filter(
                                                  id => id !== delegateId
                                                )
                                            }))
                                          }
                                          className='ml-0.5 rounded-full hover:bg-gray-300 p-0.5'
                                        >
                                          <X className='h-2.5 w-2.5' />
                                        </button>
                                      </Badge>
                                    ) : null;
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 3: Review */}
              {currentStep === 3 && (
                <motion.div
                  key='step3'
                  initial={{
                    opacity: 0,
                    x: animationDirection === 1 ? 100 : -100
                  }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 100 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className='max-w-4xl mx-auto space-y-6'
                >
                  {/* Summary */}
                  <div className='bg-muted/30 rounded-lg p-6 space-y-4'>
                    <h3 className='text-lg font-semibold mb-4'>
                      Quote Summary
                    </h3>

                    <div className='space-y-3'>
                      <div className='flex justify-between py-2 border-b'>
                        <span className='text-sm text-muted-foreground'>
                          Course
                        </span>
                        <span className='font-medium'>{courseSearch}</span>
                      </div>
                      <div className='flex justify-between py-2 border-b'>
                        <span className='text-sm text-muted-foreground'>
                          Delivery Type
                        </span>
                        <span className='font-medium capitalize'>
                          {formData.trainingType.join(', ')}
                        </span>
                      </div>
                      {formData.trainingLocation && (
                        <div className='flex justify-between py-2 border-b'>
                          <span className='text-sm text-muted-foreground'>
                            Location
                          </span>
                          <span className='font-medium'>
                            {formData.trainingLocation}
                          </span>
                        </div>
                      )}
                      <div className='flex justify-between py-2 border-b'>
                        <span className='text-sm text-muted-foreground'>
                          Date
                        </span>
                        <span className='font-medium'>
                          {formData.dateType === 'flexible'
                            ? formData.flexibleMonths || 'Flexible'
                            : formData.dateType === 'specific' && formData.specificDates.length > 0
                            ? formData.specificDates.length === 1
                              ? format(formData.specificDates[0], 'PPP')
                              : `${formData.specificDates.length} dates selected`
                            : formData.dateType === 'asap'
                            ? 'ASAP'
                            : 'Not selected'}
                        </span>
                      </div>
                      {formData.dateType === 'specific' && formData.specificDates.length > 1 && (
                        <div className='py-2 border-b'>
                          <span className='text-sm text-muted-foreground mb-2 block'>
                            Selected dates:
                          </span>
                          <div className='flex flex-wrap gap-1.5'>
                            {formData.specificDates
                              .sort((a, b) => a - b)
                              .map((date, index) => (
                                <Badge
                                  key={index}
                                  variant='secondary'
                                  className='text-xs'
                                >
                                  {format(date, 'MMM dd, yyyy')}
                                </Badge>
                              ))}
                          </div>
                        </div>
                      )}
                      <div className='flex justify-between py-2'>
                        <span className='text-sm text-muted-foreground'>
                          Delegates
                        </span>
                        <span className='font-medium'>
                          {formData.selectedDelegates.length ||
                            formData.delegateCount ||
                            0}
                        </span>
                      </div>
                    </div>

                    {formData.selectedDelegates.length > 0 && (
                      <div className='pt-4 border-t'>
                        <p className='text-sm font-medium mb-2'>
                          Selected Team Members:
                        </p>
                        <div className='flex flex-wrap gap-2'>
                          {formData.selectedDelegates.map(id => {
                            const member = teamMembers.find(m => m.id === id);
                            return member ? (
                              <Badge key={id} variant='secondary'>
                                {member.name}
                              </Badge>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Additional Notes */}
                  <div className='space-y-2'>
                    <Label className='text-base font-medium'>
                      Additional Notes (Optional)
                    </Label>
                    <Textarea
                      placeholder='Add any specific requirements, preferences, or questions...'
                      value={formData.notes}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          notes: e.target.value
                        }))
                      }
                      rows={5}
                      className='resize-none bg-white border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all'
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer Navigation */}
          <div className='px-8 py-4 bg-gray-50 border-t flex justify-between'>
            <div>
              {currentStep > 1 && (
                <Button
                  variant='outline'
                  onClick={handlePrevious}
                  className='flex items-center gap-2'
                >
                  <ArrowLeft className='w-4 h-4' />
                  Previous
                </Button>
              )}
            </div>

            <div>
              {currentStep < 3 ? (
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    onClick={handleNext}
                    className='bg-black hover:bg-gray-900 text-white flex items-center gap-2 shadow-lg hover:shadow-xl transition-all'
                    disabled={!canProceed()}
                  >
                    Next
                    <ArrowRight className='w-4 h-4' />
                  </Button>
                </motion.div>
              ) : (
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    onClick={handleSubmit}
                    className='bg-black hover:bg-gray-900 text-white shadow-lg hover:shadow-xl transition-all px-6'
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                        Creating Quote...
                      </>
                    ) : (
                      <>
                        <Check className='mr-2 h-4 w-4' />
                        Submit Quote Request
                      </>
                    )}
                  </Button>
                </motion.div>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Create Custom Course Modal */}
      <CreateCustomCourse
        isOpen={isCreateCourseOpen}
        onClose={handleCreateCourseClose}
        onCancel={handleCreateCourseCancel}
        initialTitle={newCourseTitle}
        onSuccess={handleCustomCourseCreated}
      />
    </AppShell3>
  );
}
